<template>
  <div class="mt-6 ml-4 flex space-between space-x-4 justify-between m-2 mr-4 flex-end">
    <div>
      <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">{{ t('mcpConnections.title') || 'MCP Connections' }}</h1>
      <h1 class="text text-gray-900 dark:text-gray-200">{{ t('mcpConnections.subtitle') || 'Manage Model Context Protocol connections' }}</h1>
    </div>
    <div class="flex items-center">
      <div class="dropdown dropdown-end">
        <button class="btn" @click="toggleAddDropdown">{{ t('mcpConnections.addNew') || 'Add New Connection' }}</button>
        <ul v-if="showAddDropdown === true" class="menu dropdown-content bg-base-100 rounded-box z-50 w-64 p-2 shadow mt-2">
          <li v-for="tname in supportedTypes" :key="tname">
            <button type="button" class="w-full text-left" @click.stop.prevent="chooseType(tname)">{{ getTypeDisplay(tname) }}</button>
          </li>
        </ul>
      </div>
    </div>
  </div>

  <div class="mt-6 px-4">
    <ThemeOnly theme="modern" :invert="true">
      <div class="overflow-x-auto text-align card shadow bg-white dark:bg-gray-800 w-full rounded-md p-3 border dark:border-gray-700 flex" style="border-radius: 10px; width:98%;height: 70vh;">
        <EasyDataTable :headers="headers" :items="items" buttons-pagination :search-field="['type','name','endpoint']" :search-value="searchValue" :rows-per-page="6" :rows-items="[6, 12, 24, 48]">
      <template #item-type="item">
        <div class="flex items-center space-x-2">
          <span class="uppercase text-xs px-2 py-1 rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200">{{ item.type }}</span>
        </div>
      </template>
      <template #item-endpoint="item">
        <span class="truncate block max-w-[320px]">{{ formatEndpoint(item) }}</span>
      </template>
      <template #item-options="item">
        <button @click="edit(item.mcpId)" :class="editButtonClass">{{ t('common.edit') || 'Edit' }}</button>
        <button @click="removeItem(item.mcpId)" :class="deleteButtonClass">{{ t('common.delete') || 'Delete' }}</button>
      </template>
      <template #loading>
        <ModernLoader />
      </template>
    </EasyDataTable>
      </div>
    </ThemeOnly>

    <ThemeOnly theme="modern">
      <ModernAgentTable 
        :headers="headers" 
        :items="items" 
        :loading="items.length === 0"
      >
        <template #item-type="{ item }">
          <span class="type-pill type-private uppercase text-[10px]">{{ item.type }}</span>
        </template>
        <template #item-endpoint="{ item }">
          <span class="truncate block max-w-[200px] text-[10px] font-mono">{{ formatEndpoint(item) }}</span>
        </template>
        <template #item-options="{ item }">
          <div class="flex items-center justify-end space-x-2">
            <button @click="edit(item.mcpId)" class="action-btn">
              <Icon icon="tabler:edit" width="16" />
              <span class="btn-text">{{ t('common.edit') || 'Edit' }}</span>
            </button>
            <button @click="removeItem(item.mcpId)" class="action-btn btn-delete">
              <Icon icon="tabler:trash" width="16" />
              <span class="btn-text">{{ t('common.delete') || 'Delete' }}</span>
            </button>
          </div>
        </template>
      </ModernAgentTable>
    </ThemeOnly>
  </div>

  <MCPConnectionModal
    :visible="showModal"
    :type="current.type"
    :model="current.config"
    :isEdit="isEdit"
    @close="closeModal"
    @save="onModalSave"
  />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from '../api/axiosInstance'
import EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import { globalState } from '@/globalState'
import MCPConnectionModal from '@/components/MCPConnectionModal.vue'
import { useToast } from 'vue-toastification'
import { useI18n } from '@/i18n.js'
import ModernLoader from '@/components/ModernLoader.vue'
import ThemeOnly from '@/components/ThemeOnly.vue'
import ModernAgentTable from '@/components/ModernAgentTable.vue'
import { Icon } from '@iconify/vue'

const { t } = useI18n()
const toast = useToast()

const searchValue = ref('')
const items = ref([])
const loading = ref(false)
const showAddDropdown = ref(false)
const showModal = ref(false)
const isEdit = ref(false)
const current = ref({ mcpId: undefined, type: '', name: '', endpoint: '', config: {} })

const supportedTypes = [
  'filesystem',
  'postgres',
  'slack',
  'github',
  'weaviate',
  'qdrant',
  'elasticsearch',
  'openapi',
  'web-search'
]

const editButtonClass = 'mr-4 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
const deleteButtonClass = 'hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'

const headers = computed(() => [
  { text: t('mcpConnections.headers.id') || 'ID', value: 'mcpId', sortable: true },
  { text: t('mcpConnections.headers.type') || 'Type', value: 'type', sortable: true },
  { text: t('mcpConnections.headers.name') || 'Name', value: 'name', sortable: true },
  { text: t('mcpConnections.headers.endpoint') || 'Endpoint', value: 'endpoint', sortable: false },
  { text: t('mcpConnections.headers.created') || 'Created', value: 'createdAt', sortable: true },
  { text: t('mcpConnections.headers.updated') || 'Updated', value: 'updatedAt', sortable: true },
  { text: t('mcpConnections.headers.options') || 'Options', value: 'options' }
])

function toggleAddDropdown() {
  showAddDropdown.value = showAddDropdown.value === true ? false : true
}

function getTypeDisplay(type) {
  if (type === 'filesystem') {
    return 'Filesystem'
  }
  if (type === 'postgres') {
    return 'Postgres'
  }
  if (type === 'slack') {
    return 'Slack'
  }
  if (type === 'github') {
    return 'GitHub'
  }
  if (type === 'weaviate') {
    return 'Weaviate'
  }
  if (type === 'qdrant') {
    return 'Qdrant'
  }
  if (type === 'elasticsearch') {
    return 'Elasticsearch'
  }
  if (type === 'openapi') {
    return 'OpenAPI'
  }
  if (type === 'web-search') {
    return 'Web Search'
  }
  return type
}

function chooseType(type) {
  showAddDropdown.value = false
  isEdit.value = false
  current.value = { mcpId: undefined, type, name: '', endpoint: '', config: {} }
  showModal.value = true
}

function closeModal() {
  showModal.value = false
}

function formatEndpoint(item) {
  const hasEndpoint = typeof item?.endpoint === 'string' && item.endpoint.length > 0
  if (hasEndpoint === true) {
    return item.endpoint
  }
  return ''
}

async function loadList() {
  loading.value = true
  try {
    const res = await axios.get(`${globalState.apiEndpoint}/api/mcps/list`)
    items.value = Array.isArray(res.data) === true ? res.data : []
  } finally {
    loading.value = false
  }
}

async function save() {
  try {
    const isEditMode = isEdit.value === true && current.value.mcpId !== undefined
    const nameVal = typeof current.value.name === 'string' && current.value.name.trim().length > 0 ? current.value.name.trim() : ''
    if (nameVal.length === 0) {
      toast.error(t('mcpConnections.toasts.nameRequired') || 'Name is required')
      return
    }
    if (isEditMode === true) {
      await axios.put(`${globalState.apiEndpoint}/api/mcps/modify/${current.value.mcpId}`, { name: nameVal, type: current.value.type, endpoint: current.value.endpoint, config: current.value.config })
    } else {
      await axios.post(`${globalState.apiEndpoint}/api/mcps/create`, { name: nameVal, type: current.value.type, endpoint: current.value.endpoint, config: current.value.config })
    }
    showModal.value = false
    await loadList()
    toast.success(t('mcpConnections.toasts.savedSuccess') || 'Saved successfully')
  } catch (error) {
    let message = t('mcpConnections.toasts.failedToSave') || 'Failed to save'
    if (error !== undefined && error !== null) {
      const statusText = typeof error?.response?.status === 'number' ? ` (${error.response.status})` : ''
      const serverMsg = typeof error?.response?.data?.error === 'string' && error.response.data.error.length > 0 ? error.response.data.error : ''
      const serverDetails = typeof error?.response?.data?.details === 'string' && error.response.data.details.length > 0 ? `: ${error.response.data.details}` : ''
      if (serverMsg.length > 0 || serverDetails.length > 0) {
        message = `${serverMsg}${serverDetails}${statusText}`.trim()
      }
    }
    toast.error(message)
  }
}

async function edit(mcpId) {
  const res = await axios.get(`${globalState.apiEndpoint}/api/mcps/${mcpId}`)
  current.value = { ...res.data }
  isEdit.value = true
  showModal.value = true
}

async function removeItem(mcpId) {
  await axios.delete(`${globalState.apiEndpoint}/api/mcps/delete/${mcpId}`)
  await loadList()
}

onMounted(() => {
  loadList()
})

async function onModalSave(form) {
  try {
    current.value = { ...current.value, ...form }
    await save()
  } catch (error) {
  }
}
</script>

<style>
.vue3-easy-data-table { height: 95%; }
</style>

 